import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiUsers,
  FiCalendar,
  FiBook,
  FiPlus,
  FiLayers,
  FiClock,
  FiVideo,
  FiBarChart2,
  FiTrendingUp,
  FiFileText,
  FiLink,
  FiCopy,
  FiSave,
  FiX,
  FiCheck,
  FiUnlock,
  FiLock,
  FiActivity,
  FiZap,
  FiInfo,
  FiUser,
  FiCheckCircle
} from "react-icons/fi";
import adminApi from "../../api/adminApi";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import Table from "../../components/Table/Table";
import "./BatchDetail.css";

const BatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [modules, setModules] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [progress, setProgress] = useState(null);
  const [resources, setResources] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showBulkModuleModal, setShowBulkModuleModal] = useState(false);
  const [showLectureModal, setShowLectureModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showModuleDetailModal, setShowModuleDetailModal] = useState(false);

  const [assignType, setAssignType] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]);

  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [bulkModuleLoading, setBulkModuleLoading] = useState(false);
  const [lectureLoading, setLectureLoading] = useState(false);
  const [openModule, setOpenModule] = useState(null);
  const [moduleStats, setModuleStats] = useState({});
  const [teachersData, setTeachersData] = useState([]);
  const [students, setStudents] = useState([]);

  const [formData, setFormData] = useState({
    batchName: "",
    startDate: "",
    endDate: "",
    status: "upcoming",
  });

  const [moduleFormData, setModuleFormData] = useState({
    title: "",
    description: "",
    order: "",
    duration: "",
    isOpen: false,
  });

  const [bulkModulesData, setBulkModulesData] = useState([
    { title: "", description: "", order: 1, duration: "", isOpen: false },
  ]);

  const [lectureFormData, setLectureFormData] = useState({
    module: "",
    teacher: "",
    topic: "",
    description: "",
    startTime: "",
    endTime: "",
    meetingLink: "",
    recordingLink: "",
    createFermion: false,
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [instructorType, setInstructorType] = useState("self"); // "self" or "instructor"

  const [resourceFormData, setResourceFormData] = useState({
    title: "",
    type: "link",
    url: "",
    description: "",
  });

  useEffect(() => {
    // Load current user from localStorage
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (err) {
        console.error("Failed to parse user data:", err);
      }
    }
    loadBatch();
    loadModules();
    loadLectures();
    loadOpenModule();
    loadStudents();
  }, [batchId]);

  // Reload progress and analytics when batch, modules, or lectures change
  useEffect(() => {
    if (batch && modules.length >= 0) {
      loadProgress();
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch?._id, modules.length, lectures.length]);

  // Reload module stats when modules or lectures change
  useEffect(() => {
    if (modules.length > 0 && lectures.length >= 0) {
      loadModuleStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules.length, lectures.length]);

  const loadBatch = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get(`/batches/${batchId}`);
      console.log("🔍 [BatchDetail] Full API Response:", response);
      console.log("🔍 [BatchDetail] Batch Data:", response.data);
      console.log("🔍 [BatchDetail] TeacherIds:", response.data?.teacherIds);
      console.log("🔍 [BatchDetail] Teachers:", response.data?.teachers);
      console.log("🔍 [BatchDetail] Students:", response.data?.students);
      console.log(
        "🔍 [BatchDetail] StudentCount:",
        response.data?.studentCount
      );

      // Log first teacher structure if exists
      if (response.data?.teacherIds?.length > 0) {
        console.log(
          "🔍 [BatchDetail] First Teacher Structure:",
          response.data.teacherIds[0]
        );
        console.log(
          "🔍 [BatchDetail] First Teacher Keys:",
          Object.keys(response.data.teacherIds[0])
        );
      }

      setBatch(response.data);
      setFormData({
        batchName: response.data.batchName || "",
        startDate: response.data.startDate
          ? response.data.startDate.split("T")[0]
          : "",
        endDate: response.data.endDate
          ? response.data.endDate.split("T")[0]
          : "",
        status: response.data.status || "upcoming",
      });

      // Load teacher details if teacherIds are present
      if (response.data?.teacherIds?.length > 0) {
        const firstTeacher = response.data.teacherIds[0];
        // Check if teacher data is populated (has user.firstName/email) or just has _id
        const hasUserData =
          firstTeacher &&
          firstTeacher.user &&
          (firstTeacher.user.firstName || firstTeacher.user.email);
        const hasDirectData =
          firstTeacher && (firstTeacher.firstName || firstTeacher.email);

        if (!hasUserData && !hasDirectData) {
          console.log(
            "🔍 [BatchDetail] TeacherIds not populated, fetching teacher details..."
          );
          await loadTeachersData(response.data.teacherIds);
        } else {
          // Teachers are already populated - extract user data
          const populatedTeachers = response.data.teacherIds.map((teacher) => {
            // Handle different response formats from backend
            // Format 1: Teacher document with populated user
            if (teacher.user && typeof teacher.user === "object") {
              return {
                _id: teacher._id || teacher.teacherId,
                teacherId: teacher._id || teacher.teacherId,
                firstName: teacher.user.firstName || "",
                lastName: teacher.user.lastName || "",
                email: teacher.user.email || "",
                user: teacher.user,
              };
            }
            // Format 2: Direct user fields (from backend transformation)
            if (teacher.firstName || teacher.email) {
              return {
                _id: teacher._id || teacher.teacherId || teacher.id,
                teacherId: teacher._id || teacher.teacherId || teacher.id,
                firstName: teacher.firstName || "",
                lastName: teacher.lastName || "",
                email: teacher.email || "",
                user: teacher,
              };
            }
            // Format 3: Just ID - will be handled by loadTeachersData
            return {
              _id: teacher._id || teacher,
              teacherId: teacher._id || teacher,
              firstName: "Loading...",
              lastName: "",
              email: "",
            };
          });

          // Check if any teachers need to be loaded (just IDs without data)
          const needsLoading = populatedTeachers.some(
            (t) => !t.firstName || t.firstName === "Loading..."
          );

          if (needsLoading) {
            await loadTeachersData(response.data.teacherIds);
          } else {
            console.log(
              "🔍 [BatchDetail] Using populated teacher data:",
              populatedTeachers.length,
              "teachers"
            );
            setTeachersData(populatedTeachers);
          }
        }
      } else {
        // No teachers assigned
        setTeachersData([]);
      }
    } catch (err) {
      console.error("❌ [BatchDetail] Error loading batch:", err);
      console.error("❌ [BatchDetail] Error response:", err.response);
      setError(err.response?.data?.message || "Failed to load batch");
    } finally {
      setLoading(false);
    }
  };

  const loadTeachersData = async (teacherIds) => {
    try {
      console.log(
        "🔍 [BatchDetail] Loading teachers data for IDs:",
        teacherIds
      );

      // Extract teacher IDs - handle both populated and non-populated teacherIds
      const teacherIdStrings = teacherIds.map((t) => {
        // If it's already an object with _id, use that
        if (typeof t === "object" && t._id) {
          return t._id.toString();
        }
        // If it's a string, use it directly
        if (typeof t === "string") {
          return t;
        }
        // Otherwise, try to convert to string
        return String(t);
      });

      console.log("🔍 [BatchDetail] Teacher ID strings:", teacherIdStrings);

      // Fetch all instructors (Teacher documents with populated User data)
      const instructorsResponse = await adminApi.get("/instructors", {
        params: { limit: 1000 },
      });

      const allInstructors =
        instructorsResponse.data?.data ||
        instructorsResponse.data?.instructors ||
        instructorsResponse.data ||
        [];

      console.log(
        "🔍 [BatchDetail] All instructors from API:",
        allInstructors.length,
        "instructors found"
      );

      // Match teacherIds to instructors by Teacher document ID
      const matchedTeachers = [];

      for (const teacherId of teacherIdStrings) {
        // Find instructor by Teacher document ID (instructor._id is the Teacher ID)
        const matchedInstructor = allInstructors.find(
          (inst) => inst._id?.toString() === teacherId.toString()
        );

        if (matchedInstructor && matchedInstructor.user) {
          // Extract user data from instructor
          const userData = matchedInstructor.user;
          matchedTeachers.push({
            _id: teacherId,
            teacherId: matchedInstructor._id,
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "",
            user: userData,
            // Include instructor data for reference
            instructor: matchedInstructor,
          });
          console.log(
            `✅ [BatchDetail] Matched teacher ${teacherId} to instructor:`,
            userData.firstName,
            userData.lastName
          );
        } else {
          // If not found in instructors, check if teacherId is already populated with user data
          const existingTeacher = teacherIds.find(
            (t) => (t._id || t)?.toString() === teacherId.toString()
          );

          if (
            existingTeacher &&
            (existingTeacher.user || existingTeacher.firstName)
          ) {
            // Use the already populated data
            matchedTeachers.push({
              _id: teacherId,
              teacherId: existingTeacher.teacherId || teacherId,
              firstName:
                existingTeacher.user?.firstName ||
                existingTeacher.firstName ||
                "",
              lastName:
                existingTeacher.user?.lastName ||
                existingTeacher.lastName ||
                "",
              email: existingTeacher.user?.email || existingTeacher.email || "",
              user: existingTeacher.user || existingTeacher,
            });
            console.log(
              `✅ [BatchDetail] Using already populated teacher data for ${teacherId}`
            );
          } else {
            // Last resort: create placeholder
            console.warn(
              `⚠️ [BatchDetail] No match found for teacher ID: ${teacherId}`
            );
            matchedTeachers.push({
              _id: teacherId,
              teacherId: teacherId,
              firstName: "Unknown",
              lastName: "Teacher",
              email: "",
            });
          }
        }
      }

      console.log(
        "🔍 [BatchDetail] Final matched teachers:",
        matchedTeachers.length,
        "teachers"
      );
      console.log("🔍 [BatchDetail] TeachersData:", matchedTeachers);
      setTeachersData(matchedTeachers);
    } catch (err) {
      console.error("❌ [BatchDetail] Error loading teachers data:", err);
      console.error("❌ [BatchDetail] Error details:", err.response?.data);
      // Fallback: use teacherIds as-is or extract from populated data
      const fallbackTeachers = teacherIds.map((t) => {
        if (typeof t === "object") {
          return {
            _id: t._id || t,
            teacherId: t._id || t,
            firstName: t.user?.firstName || t.firstName || "Unknown",
            lastName: t.user?.lastName || t.lastName || "Teacher",
            email: t.user?.email || t.email || "",
            user: t.user || t,
          };
        }
        return {
          _id: t,
          teacherId: t,
          firstName: "Unknown",
          lastName: "Teacher",
          email: "",
        };
      });
      setTeachersData(fallbackTeachers);
    }
  };

  const loadModules = async () => {
    try {
      console.log("🔍 [BatchDetail] Loading modules for batch:", batchId);
      const response = await adminApi.get(`/batches/${batchId}/modules`);
      console.log("🔍 [BatchDetail] Modules API Response:", response);
      const modulesList =
        response.data?.modules || response.data?.data || response.data || [];
      console.log("🔍 [BatchDetail] Modules List:", modulesList);
      // Sort by order
      const sortedModules = [...modulesList].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      console.log("🔍 [BatchDetail] Sorted Modules:", sortedModules);
      setModules(sortedModules);
    } catch (err) {
      console.error("❌ [BatchDetail] Failed to load modules:", err);
      console.error("❌ [BatchDetail] Error response:", err.response);
      setModules([]);
    }
  };

  const loadStudents = async () => {
    try {
      console.log("🔍 [BatchDetail] Loading students for batch:", batchId);
      // Fetch enrollments for this batch
      const response = await adminApi.get("/enrollments", {
        params: { batchId, limit: 1000, status: "active" },
      });
      console.log("🔍 [BatchDetail] Enrollments API Response:", response);
      const enrollments =
        response.data?.enrollments ||
        response.data?.data ||
        response.data ||
        [];
      console.log("🔍 [BatchDetail] Enrollments List:", enrollments);

      // Extract students from enrollments
      const studentsList = enrollments
        .filter((enrollment) => enrollment.user)
        .map((enrollment) => ({
          _id: enrollment.user._id || enrollment.user.id,
          ...enrollment.user,
          enrollmentId: enrollment._id || enrollment.id,
          enrollmentStatus: enrollment.status,
        }));

      console.log("🔍 [BatchDetail] Students List:", studentsList);
      setStudents(studentsList);
    } catch (err) {
      console.error("❌ [BatchDetail] Failed to load students:", err);
      console.error("❌ [BatchDetail] Error response:", err.response);
      setStudents([]);
    }
  };

  const loadOpenModule = async () => {
    try {
      const response = await adminApi.get(
        `/batches/${batchId}/open-module`
      );
      setOpenModule(response.data);
    } catch (err) {
      // No open module or endpoint doesn't exist
      setOpenModule(null);
    }
  };

  const loadModuleStats = async () => {
    try {
      const stats = {};
      modules.forEach((module) => {
        const moduleLectures = lectures.filter(
          (l) => l.module?._id === module._id || l.module === module._id
        );
        const completedLectures = moduleLectures.filter(
          (l) => l.endTime && new Date(l.endTime) < new Date()
        );
        const totalStudents = batch?.students?.length || 0;

        stats[module._id] = {
          totalLectures: moduleLectures.length,
          completedLectures: completedLectures.length,
          completionRate:
            moduleLectures.length > 0
              ? (
                  (completedLectures.length / moduleLectures.length) *
                  100
                ).toFixed(1)
              : 0,
          totalStudents,
          duration: module.duration || "—",
        };
      });
      setModuleStats(stats);
    } catch (err) {
      console.error("Failed to load module stats:", err);
    }
  };

  const loadLectures = async () => {
    try {
      const response = await adminApi.get(
        `/batches/${batchId}/lectures`,
        {
          params: { limit: 100 },
        }
      );
      setLectures(response.data?.lectures || response.data?.data || []);
    } catch (err) {
      console.error("Failed to load lectures:", err);
      setLectures([]);
    }
  };

  const loadProgress = async () => {
    try {
      // Calculate progress from modules and students
      const studentsCount = batch?.students?.length || 0;
      const modulesCount = modules.length;
      const completedModules = modules.filter((m) => m.isOpen).length;
      const progressPercent =
        modulesCount > 0 ? (completedModules / modulesCount) * 100 : 0;

      setProgress({
        totalModules: modulesCount,
        completedModules,
        progressPercent: progressPercent.toFixed(1),
        studentsCount,
        lecturesCount: lectures.length,
        upcomingLectures: lectures.filter(
          (l) => new Date(l.startTime) > new Date()
        ).length,
        completedLectures: lectures.filter(
          (l) => new Date(l.endTime) < new Date()
        ).length,
      });
    } catch (err) {
      console.error("Failed to load progress:", err);
    }
  };

  const loadResources = async () => {
    try {
      // Resources might be stored in batch or separate endpoint
      // For now, using a placeholder - adjust based on actual API
      setResources(batch?.resources || []);
    } catch (err) {
      console.error("Failed to load resources:", err);
      setResources([]);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Calculate analytics
      const totalStudents = batch?.students?.length || 0;
      const totalLectures = lectures.length;
      const completedLectures = lectures.filter(
        (l) => new Date(l.endTime) < new Date()
      ).length;
      const attendanceRate =
        totalLectures > 0 ? (completedLectures / totalLectures) * 100 : 0;

      setAnalytics({
        totalStudents,
        totalLectures,
        completedLectures,
        attendanceRate: attendanceRate.toFixed(1),
        averageCompletion: progress?.progressPercent || 0,
      });
    } catch (err) {
      console.error("Failed to load analytics:", err);
    }
  };

  const loadAvailableUsers = async (role) => {
    try {
      // For teachers, fetch instructors (Teacher documents) instead of users
      if (role === "mentor") {
        const response = await adminApi.get("/instructors", {
          params: { limit: 1000, status: "active" },
        });
        const instructorsList =
          response.data?.data ||
          response.data?.instructors ||
          response.data ||
          [];
        console.log(
          "🔍 [BatchDetail] Loaded instructors for assignment:",
          instructorsList
        );
        setAvailableInstructors(instructorsList);
        // Transform instructors to a format compatible with the modal (using User data for display)
        const transformedList = instructorsList.map((instructor) => ({
          _id: instructor._id, // Teacher document ID (what we'll send to backend)
          teacherId: instructor._id,
          firstName: instructor.user?.firstName || instructor.firstName || "",
          lastName: instructor.user?.lastName || instructor.lastName || "",
          email: instructor.user?.email || instructor.email || "",
          user: instructor.user || instructor,
          // Keep full instructor object for reference
          instructor: instructor,
        }));
        setAvailableUsers(transformedList);
      } else {
        // For students, fetch users as before
        const response = await adminApi.get("/users", {
          params: { role, limit: 100 },
        });
        const usersList = response.data?.users || response.data?.data || [];
        setAvailableUsers(usersList);
      }
    } catch (err) {
      console.error("Failed to load users/instructors:", err);
      setAvailableUsers([]);
      if (role === "mentor") {
        setAvailableInstructors([]);
      }
    }
  };

  const loadAvailableTeachers = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "mentor", limit: 100 },
      });
      const teachersList = response.data?.users || response.data?.data || [];
      // Also include batch teachers if available
      const batchTeachers = batch?.teachers || [];
      const allTeachers = [
        ...teachersList,
        ...batchTeachers.filter(
          (bt) => !teachersList.find((t) => t._id === bt._id)
        ),
      ];
      setAvailableTeachers(allTeachers);
    } catch (err) {
      console.error("Failed to load teachers:", err);
      // Fallback to batch teachers
      setAvailableTeachers(batch?.teachers || []);
    }
  };

  // Update progress when modules or lectures change
  useEffect(() => {
    if (batch && modules.length > 0) {
      loadProgress();
      loadAnalytics();
    }
  }, [modules, lectures, batch]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const response = await adminApi.put(
        `/batches/${batchId}`,
        formData
      );
      setBatch(response.data);
      setShowEditModal(false);
      loadBatch();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update batch");
    } finally {
      setEditLoading(false);
    }
  };

  const handleAssign = async () => {
    setAssignLoading(true);
    try {
      const endpoint =
        assignType === "teachers"
          ? `/batches/${batchId}/teachers`
          : `/batches/${batchId}/students`;
      const field = assignType === "teachers" ? "teacherIds" : "studentIds";

      // For teachers, selectedIds are already Teacher document IDs (instructor._id)
      // For students, selectedIds are User IDs
      // Both can be sent directly as they are in the correct format

      if (assignType === "teachers") {
        console.log(
          "🔍 [BatchDetail] Assigning teachers with Teacher IDs:",
          selectedIds
        );
      }

      await adminApi.patch(endpoint, { [field]: selectedIds });
      setShowAssignModal(false);
      setSelectedIds([]);
      setAssignType(null);
      loadBatch();
    } catch (err) {
      console.error("❌ [BatchDetail] Error assigning:", err);
      console.error("❌ [BatchDetail] Error response:", err.response?.data);
      setError(err.response?.data?.message || "Failed to assign");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await adminApi.delete(`/batches/${batchId}`);
      navigate("/batches");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete batch");
      setDeleteLoading(false);
    }
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    setModuleLoading(true);
    try {
      if (selectedModule) {
        // Update module
        await adminApi.patch(
          `/modules/${selectedModule._id}`,
          moduleFormData
        );
      } else {
        // Create module
        await adminApi.post(
          `/batches/${batchId}/modules`,
          moduleFormData
        );
      }
      setShowModuleModal(false);
      setSelectedModule(null);
      setModuleFormData({
        title: "",
        description: "",
        order: "",
        duration: "",
        isOpen: false,
      });
      loadModules();
      loadOpenModule();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save module");
    } finally {
      setModuleLoading(false);
    }
  };

  const handleBulkModuleSubmit = async (e) => {
    e.preventDefault();
    setBulkModuleLoading(true);
    try {
      // Filter out empty modules
      const validModules = bulkModulesData.filter(
        (m) => m.title.trim() && m.order
      );

      if (validModules.length === 0) {
        setError("Please add at least one module");
        setBulkModuleLoading(false);
        return;
      }

      await adminApi.post(
        `/batches/${batchId}/modules/bulk`,
        validModules
      );
      setShowBulkModuleModal(false);
      setBulkModulesData([
        { title: "", description: "", order: 1, duration: "", isOpen: false },
      ]);
      loadModules();
      loadOpenModule();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create modules");
    } finally {
      setBulkModuleLoading(false);
    }
  };

  const handleReorderModules = async (moduleId, newOrder) => {
    try {
      await adminApi.patch(`/modules/${moduleId}`, { order: newOrder });
      loadModules();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reorder module");
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Are you sure you want to delete this module?")) return;
    try {
      await adminApi.delete(`/modules/${moduleId}`);
      loadModules();
      loadOpenModule();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete module");
    }
  };

  const handleToggleModule = async (moduleId, isOpen) => {
    try {
      const endpoint = isOpen
        ? `/modules/${moduleId}/close`
        : `/modules/${moduleId}/open`;
      await adminApi.patch(endpoint);
      loadModules();
      loadOpenModule();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to toggle module");
    }
  };

  const handleLectureSubmit = async (e) => {
    e.preventDefault();
    setLectureLoading(true);
    try {
      const lectureData = {
        topic: lectureFormData.topic,
        description: lectureFormData.description,
        startTime: new Date(lectureFormData.startTime).toISOString(),
        endTime: new Date(lectureFormData.endTime).toISOString(),
        meetingLink: lectureFormData.meetingLink,
        recordingLink: lectureFormData.recordingLink,
      };

      // Only include createFermion if it's true
      if (lectureFormData.createFermion) {
        lectureData.createFermion = true;
      }

      // Only include module if it's provided and not empty
      if (
        lectureFormData.module &&
        String(lectureFormData.module).trim() !== ""
      ) {
        lectureData.module = lectureFormData.module;
      }

      // Handle teacher assignment based on instructor type
      const currentUserData =
        currentUser ||
        (() => {
          const storedUser = localStorage.getItem("user");
          return storedUser ? JSON.parse(storedUser) : null;
        })();

      if (instructorType === "self" && currentUserData) {
        // Admin/superadmin taking the lecture themselves
        lectureData.teacher = currentUserData._id;
      } else if (
        instructorType === "instructor" &&
        lectureFormData.teacher &&
        String(lectureFormData.teacher).trim() !== ""
      ) {
        // Assigning to an instructor
        lectureData.teacher = lectureFormData.teacher;
      }
      // If neither is set, don't include teacher field (optional)

      if (selectedLecture) {
        // Update lecture
        await adminApi.put(
          `/lectures/${selectedLecture._id}`,
          lectureData
        );
      } else {
        // Create lecture - batchId is in URL, don't include in body
        await adminApi.post(`/batches/${batchId}/lectures`, lectureData);
      }
      setShowLectureModal(false);
      setSelectedLecture(null);
      const userStr = localStorage.getItem("user");
      let defaultInstructorType = "instructor";
      let defaultTeacher = "";

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user && ["admin", "superadmin"].includes(user.role)) {
            defaultInstructorType = "self";
            defaultTeacher = user._id;
          }
        } catch (err) {
          // Ignore parse errors
        }
      }

      setInstructorType(defaultInstructorType);
      setLectureFormData({
        module: "",
        teacher: defaultTeacher,
        topic: "",
        description: "",
        startTime: "",
        endTime: "",
        meetingLink: "",
        recordingLink: "",
        createFermion: false,
      });
      loadLectures();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save lecture");
    } finally {
      setLectureLoading(false);
    }
  };

  const handleDeleteLecture = async (lectureId) => {
    if (!window.confirm("Are you sure you want to delete this lecture?"))
      return;
    try {
      await adminApi.delete(`/lectures/${lectureId}`);
      loadLectures();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete lecture");
    }
  };

  const handleResourceSubmit = async (e) => {
    e.preventDefault();
    try {
      // Resources might need a separate endpoint
      // For now, storing in batch resources array
      const updatedResources = selectedResource
        ? resources.map((r) =>
            r._id === selectedResource._id ? resourceFormData : r
          )
        : [...resources, { ...resourceFormData, _id: Date.now().toString() }];

      await adminApi.patch(`/batches/${batchId}`, {
        resources: updatedResources,
      });
      setShowResourceModal(false);
      setSelectedResource(null);
      setResourceFormData({
        title: "",
        type: "link",
        url: "",
        description: "",
      });
      loadResources();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save resource");
    }
  };

  const handleDuplicate = async () => {
    try {
      const duplicateData = {
        ...formData,
        batchName: `${formData.batchName} (Copy)`,
      };
      const response = await adminApi.post("/batches", duplicateData);
      navigate(`/batches/${response.data._id}`);
      setShowDuplicateModal(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to duplicate batch");
    }
  };

  const handleSaveAsTemplate = async () => {
    try {
      const templateName =
        prompt("Enter template name:") || `${formData.batchName} Template`;
      await adminApi.post("/batches/templates", {
        ...formData,
        templateName,
        isTemplate: true,
      });
      setShowTemplateModal(false);
      alert("Batch saved as template successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save template");
    }
  };

  const openAssignModal = (type) => {
    setAssignType(type);
    setShowAssignModal(true);
    loadAvailableUsers(type === "teachers" ? "mentor" : "student");
  };

  const openModuleModal = (module = null) => {
    setSelectedModule(module);
    if (module) {
      setModuleFormData({
        title: module.title || "",
        description: module.description || "",
        order: module.order || "",
        duration: module.duration || "",
        isOpen: module.isOpen || false,
      });
    } else {
      setModuleFormData({
        title: "",
        description: "",
        order: modules.length + 1,
        duration: "",
        isOpen: false,
      });
    }
    setShowModuleModal(true);
  };

  const openLectureModal = (lecture = null) => {
    setSelectedLecture(lecture);
    loadAvailableTeachers(); // Always load teachers when opening modal

    // Load current user if not already loaded
    const userStr = localStorage.getItem("user");
    if (userStr && !currentUser) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (err) {
        console.error("Failed to parse user data:", err);
      }
    }

    if (lecture) {
      // Extract teacher ID - could be nested in teacher.user or direct
      let teacherId = "";
      let detectedInstructorType = "instructor";

      if (lecture.teacher) {
        if (typeof lecture.teacher === "string") {
          teacherId = lecture.teacher;
        } else if (lecture.teacher._id) {
          teacherId = lecture.teacher._id;
        } else if (lecture.teacher.user?._id) {
          teacherId = lecture.teacher.user._id;
        }

        // Check if teacher is current user (self)
        const userStr = localStorage.getItem("user");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            if (user._id === teacherId) {
              detectedInstructorType = "self";
            }
          } catch (err) {
            // Ignore parse errors
          }
        }
      }

      // Extract module ID
      let moduleId = "";
      if (lecture.module) {
        if (typeof lecture.module === "string") {
          moduleId = lecture.module;
        } else if (lecture.module._id) {
          moduleId = lecture.module._id;
        }
      }

      setInstructorType(detectedInstructorType);
      setLectureFormData({
        module: moduleId,
        teacher: teacherId,
        topic: lecture.topic || "",
        description: lecture.description || "",
        startTime: lecture.startTime
          ? new Date(lecture.startTime).toISOString().slice(0, 16)
          : "",
        endTime: lecture.endTime
          ? new Date(lecture.endTime).toISOString().slice(0, 16)
          : "",
        meetingLink: lecture.meetingLink || "",
        recordingLink: lecture.recordingLink || "",
        createFermion: false, // When editing, don't create new Google Meet link
      });
    } else {
      // New lecture - default to self if admin/superadmin
      const userStr = localStorage.getItem("user");
      let defaultInstructorType = "instructor";
      let defaultTeacher = "";

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user && ["admin", "superadmin"].includes(user.role)) {
            defaultInstructorType = "self";
            defaultTeacher = user._id;
          }
        } catch (err) {
          // Ignore parse errors
        }
      }

      setInstructorType(defaultInstructorType);
      setLectureFormData({
        module: "",
        teacher: defaultTeacher,
        topic: "",
        description: "",
        startTime: "",
        endTime: "",
        meetingLink: "",
        recordingLink: "",
        createFermion: false,
      });
    }
    setShowLectureModal(true);
  };

  const openResourceModal = (resource = null) => {
    setSelectedResource(resource);
    if (resource) {
      setResourceFormData({
        title: resource.title || "",
        type: resource.type || "link",
        url: resource.url || "",
        description: resource.description || "",
      });
    } else {
      setResourceFormData({
        title: "",
        type: "link",
        url: "",
        description: "",
      });
    }
    setShowResourceModal(true);
  };

  if (loading) {
    return <div className="detail-loading">Loading batch details...</div>;
  }

  if (error && !batch) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate("/batches")}>Back to Batches</Button>
      </div>
    );
  }

  return (
    <div className="batch-detail-page">
      {/* Breadcrumb showing Course → Batch relationship */}
      {batch && batch.course && (
        <div className="batch-breadcrumb">
          <Link to="/courses" className="breadcrumb-link">
            Courses
          </Link>
          <span className="breadcrumb-separator">→</span>
          <Link
            to={`/courses/${
              typeof batch.course === "object" ? batch.course._id : batch.course
            }`}
            className="breadcrumb-link"
          >
            {typeof batch.course === "object" ? batch.course.title : "Course"}
          </Link>
          <span className="breadcrumb-separator">→</span>
          <span className="breadcrumb-current">{batch.batchName}</span>
        </div>
      )}
      <header className="detail-header">
        <Link to="/batches" className="back-link">
          <FiArrowLeft size={20} />
          Back to Batches
        </Link>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowDuplicateModal(true)}
          >
            <FiCopy size={18} />
            Duplicate
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(true)}
          >
            <FiSave size={18} />
            Save as Template
          </Button>
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            <FiEdit size={18} />
            Edit Batch
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            <FiTrash2 size={18} />
            Delete
          </Button>
        </div>
      </header>

      <div className="detail-content">
        <div className="detail-main">
          {/* Tabs */}
          <section className="detail-card">
            <div className="tabs">
              <button
                type="button"
                className={`tab ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "modules" ? "active" : ""}`}
                onClick={() => setActiveTab("modules")}
              >
                <FiLayers size={16} />
                Modules
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "lectures" ? "active" : ""}`}
                onClick={() => setActiveTab("lectures")}
              >
                <FiVideo size={16} />
                Lectures
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "schedule" ? "active" : ""}`}
                onClick={() => setActiveTab("schedule")}
              >
                <FiCalendar size={16} />
                Schedule
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "progress" ? "active" : ""}`}
                onClick={() => setActiveTab("progress")}
              >
                <FiTrendingUp size={16} />
                Progress
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "resources" ? "active" : ""}`}
                onClick={() => setActiveTab("resources")}
              >
                <FiFileText size={16} />
                Resources
              </button>
              <button
                type="button"
                className={`tab ${activeTab === "analytics" ? "active" : ""}`}
                onClick={() => setActiveTab("analytics")}
              >
                <FiBarChart2 size={16} />
                Analytics
              </button>
            </div>
          </section>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <>
              <section className="detail-card">
                <div className="card-header">
                  <div>
                    <h1>{batch?.batchName}</h1>
                    <StatusBadge status={batch?.status || "upcoming"} />
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <FiBook size={18} />
                    <div>
                      <span className="info-label">Course</span>
                      <strong>{batch?.course?.title || "—"}</strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiCalendar size={18} />
                    <div>
                      <span className="info-label">Start Date</span>
                      <strong>
                        {batch?.startDate
                          ? new Date(batch.startDate).toLocaleDateString()
                          : "—"}
                      </strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiCalendar size={18} />
                    <div>
                      <span className="info-label">End Date</span>
                      <strong>
                        {batch?.endDate
                          ? new Date(batch.endDate).toLocaleDateString()
                          : "—"}
                      </strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiLayers size={18} />
                    <div>
                      <span className="info-label">Total Modules</span>
                      <strong>{modules.length}</strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiUnlock size={18} />
                    <div>
                      <span className="info-label">Open Modules</span>
                      <strong>{modules.filter((m) => m.isOpen).length}</strong>
                    </div>
                  </div>
                  {openModule && (
                    <div className="info-item highlight">
                      <FiActivity size={18} />
                      <div>
                        <span className="info-label">Currently Open</span>
                        <strong>{openModule.title}</strong>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Modules Section in Overview */}
              <section className="detail-card">
                <div className="section-header">
                  <h2>Modules</h2>
                  <div className="header-actions-group">
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => setShowBulkModuleModal(true)}
                    >
                      <FiPlus size={16} />
                      Bulk Create
                    </Button>
                    <Button size="small" onClick={() => openModuleModal()}>
                      <FiPlus size={16} />
                      Add Module
                    </Button>
                  </div>
                </div>
                {(() => {
                  console.log(
                    "🔍 [BatchDetail] Rendering modules section - modules.length:",
                    modules.length
                  );
                  console.log("🔍 [BatchDetail] Modules array:", modules);
                  return modules.length > 0 ? (
                    <div className="modules-overview-list">
                      {modules.map((module, index) => {
                        console.log(
                          `🔍 [BatchDetail] Rendering module ${index}:`,
                          module
                        );
                        const stats = moduleStats[module._id] || {};
                        const isCurrentlyOpen =
                          openModule?._id === module._id ||
                          openModule?.id === module._id;
                        return (
                          <div
                            key={module._id}
                            className={`module-overview-item ${
                              isCurrentlyOpen ? "currently-open" : ""
                            } ${module.isOpen ? "open" : "closed"}`}
                          >
                            <div className="module-overview-header">
                              <div className="module-order-badge">
                                {module.order}
                              </div>
                              <div className="module-overview-content">
                                <div className="module-title-row">
                                  <strong>{module.title}</strong>
                                  {isCurrentlyOpen && (
                                    <span className="open-indicator">
                                      <FiActivity size={14} />
                                      Currently Open
                                    </span>
                                  )}
                                  <StatusBadge
                                    status={module.isOpen ? "active" : "closed"}
                                  />
                                </div>
                                {module.description && (
                                  <p className="module-description">
                                    {module.description}
                                  </p>
                                )}
                                <div className="module-meta">
                                  {module.duration && (
                                    <span>
                                      <FiClock size={14} />
                                      {module.duration}
                                    </span>
                                  )}
                                  {stats.totalLectures !== undefined && (
                                    <span>
                                      <FiVideo size={14} />
                                      {stats.totalLectures} lectures
                                    </span>
                                  )}
                                  {stats.completionRate !== undefined && (
                                    <span>
                                      <FiCheck size={14} />
                                      {stats.completionRate}% complete
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="module-overview-actions">
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={() => {
                                  setSelectedModule(module);
                                  setShowModuleDetailModal(true);
                                }}
                              >
                                View Details
                              </Button>
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={() => openModuleModal(module)}
                              >
                                <FiEdit size={14} />
                              </Button>
                              <Button
                                size="small"
                                variant="secondary"
                                onClick={() =>
                                  handleToggleModule(module._id, module.isOpen)
                                }
                                title={
                                  module.isOpen ? "Close Module" : "Open Module"
                                }
                              >
                                {module.isOpen ? (
                                  <FiLock size={14} />
                                ) : (
                                  <FiUnlock size={14} />
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="empty-state">
                      {modules.length === 0
                        ? "No modules created yet"
                        : `Loading modules... (found ${modules.length})`}
                    </p>
                  );
                })()}
              </section>

              <section className="detail-card">
                <div className="section-header">
                  <h2>Assigned Teachers</h2>
                  <Button
                    size="small"
                    onClick={() => openAssignModal("teachers")}
                  >
                    <FiPlus size={16} />
                    Add Teachers
                  </Button>
                </div>
                <div className="users-list">
                  {(() => {
                    // Use teachersData if available (populated), otherwise use batch.teacherIds
                    const teachersList =
                      teachersData.length > 0
                        ? teachersData
                        : batch?.teacherIds || batch?.teachers || [];

                    console.log(
                      "🔍 [BatchDetail] Rendering Teachers - List:",
                      teachersList
                    );
                    console.log(
                      "🔍 [BatchDetail] Teachers Length:",
                      teachersList.length
                    );
                    console.log("🔍 [BatchDetail] TeachersData:", teachersData);
                    console.log(
                      "🔍 [BatchDetail] Batch teacherIds:",
                      batch?.teacherIds
                    );

                    if (teachersList.length > 0) {
                      return teachersList.map((teacher, index) => {
                        console.log(
                          `🔍 [BatchDetail] Teacher ${index}:`,
                          teacher
                        );
                        console.log(
                          `🔍 [BatchDetail] Teacher ${index} Keys:`,
                          Object.keys(teacher)
                        );

                        // Try multiple ways to extract user data
                        // 1. Direct fields (if populated as User)
                        // 2. Nested user object (if Teacher -> User)
                        // 3. From teachersData (our fetched data)
                        const firstName =
                          teacher.firstName || teacher.user?.firstName || "";
                        const lastName =
                          teacher.lastName || teacher.user?.lastName || "";
                        const email =
                          teacher.email || teacher.user?.email || "";

                        console.log(
                          `🔍 [BatchDetail] Teacher ${index} Extracted - firstName:`,
                          firstName,
                          "lastName:",
                          lastName,
                          "email:",
                          email
                        );

                        const initials = `${firstName?.[0] || ""}${
                          lastName?.[0] || ""
                        }`.toUpperCase();
                        const fullName =
                          `${firstName} ${lastName}`.trim() ||
                          "Unknown Teacher";

                        return (
                          <div
                            key={teacher._id || teacher.id || index}
                            className="user-chip"
                          >
                            <div className="user-avatar-small">
                              {initials || "?"}
                            </div>
                            <div>
                              <strong>{fullName}</strong>
                              <span>{email || "—"}</span>
                            </div>
                          </div>
                        );
                      });
                    } else {
                      console.log(
                        "🔍 [BatchDetail] No teachers found - showing empty state"
                      );
                      return (
                        <p className="empty-state">No teachers assigned</p>
                      );
                    }
                  })()}
                </div>
              </section>

              <section className="detail-card">
                <div className="section-header">
                  <h2>Enrolled Students</h2>
                  <Button
                    size="small"
                    onClick={() => openAssignModal("students")}
                  >
                    <FiPlus size={16} />
                    Add Students
                  </Button>
                </div>
                <div className="users-list">
                  {students.length > 0 ? (
                    students.map((student) => {
                      const studentData = student.user || student;
                      return (
                        <Link
                          key={student._id || student.id}
                          to={`/users/${
                            studentData._id || studentData.id || student._id
                          }`}
                          className="user-chip link"
                        >
                          <div className="user-avatar-small">
                            {studentData.firstName?.[0] ||
                              studentData.name?.[0] ||
                              "?"}
                            {studentData.lastName?.[0] || ""}
                          </div>
                          <div>
                            <strong>
                              {studentData.firstName ||
                                studentData.name ||
                                "Unknown"}{" "}
                              {studentData.lastName || ""}
                            </strong>
                            <span>{studentData.email || "—"}</span>
                          </div>
                        </Link>
                      );
                    })
                  ) : (
                    <p className="empty-state">
                      {batch?.studentCount
                        ? `${batch.studentCount} student(s) enrolled (loading details...)`
                        : "No students enrolled"}
                    </p>
                  )}
                </div>
              </section>
            </>
          )}

          {/* Modules Tab */}
          {activeTab === "modules" && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Modules</h2>
                <div className="header-actions-group">
                  <Button
                    variant="secondary"
                    onClick={() => setShowBulkModuleModal(true)}
                  >
                    <FiPlus size={18} />
                    Bulk Create
                  </Button>
                  <Button onClick={() => openModuleModal()}>
                    <FiPlus size={18} />
                    Add Module
                  </Button>
                </div>
              </div>
              {openModule && (
                <div className="open-module-banner">
                  <FiActivity size={18} />
                  <div>
                    <strong>Currently Open Module:</strong>
                    <span>{openModule.title}</span>
                  </div>
                </div>
              )}
              {modules.length > 0 ? (
                <div className="modules-table-container">
                  <Table
                    columns={[
                      { key: "order", label: "Order" },
                      { key: "title", label: "Title" },
                      { key: "duration", label: "Duration" },
                      {
                        key: "lectures",
                        label: "Lectures",
                        render: (row) => {
                          const moduleLectures = lectures.filter(
                            (l) =>
                              l.module?._id === row._id || l.module === row._id
                          );
                          return moduleLectures.length;
                        },
                      },
                      {
                        key: "completion",
                        label: "Completion",
                        render: (row) => {
                          const stats = moduleStats[row._id] || {};
                          return stats.completionRate
                            ? `${stats.completionRate}%`
                            : "—";
                        },
                      },
                      {
                        key: "status",
                        label: "Status",
                        render: (row) => {
                          const isCurrentlyOpen =
                            openModule?._id === row._id ||
                            openModule?.id === row._id;
                          return (
                            <div className="status-group">
                              {isCurrentlyOpen && (
                                <span className="currently-open-badge">
                                  <FiActivity size={12} />
                                  Open Now
                                </span>
                              )}
                              <StatusBadge
                                status={row.isOpen ? "active" : "closed"}
                              />
                            </div>
                          );
                        },
                      },
                      {
                        key: "actions",
                        label: "Actions",
                        render: (row) => (
                          <div className="action-buttons">
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => {
                                setSelectedModule(row);
                                setShowModuleDetailModal(true);
                              }}
                              title="View Details"
                            >
                              <FiBook size={14} />
                            </Button>
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => openModuleModal(row)}
                              title="Edit"
                            >
                              <FiEdit size={14} />
                            </Button>
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() =>
                                handleToggleModule(row._id, row.isOpen)
                              }
                              title={
                                row.isOpen ? "Close Module" : "Open Module"
                              }
                            >
                              {row.isOpen ? (
                                <FiLock size={14} />
                              ) : (
                                <FiUnlock size={14} />
                              )}
                            </Button>
                            <Button
                              size="small"
                              variant="danger"
                              onClick={() => handleDeleteModule(row._id)}
                              title="Delete"
                            >
                              <FiTrash2 size={14} />
                            </Button>
                          </div>
                        ),
                      },
                    ]}
                    data={modules}
                  />
                  <div className="module-reorder-section">
                    <h3>Reorder Modules</h3>
                    <p className="info-text">
                      Change the order number to reorder modules. Lower numbers
                      appear first.
                    </p>
                    <div className="reorder-list">
                      {modules.map((module) => (
                        <div key={module._id} className="reorder-item">
                          <span className="reorder-label">{module.title}</span>
                          <div className="reorder-controls">
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() =>
                                handleReorderModules(
                                  module._id,
                                  Math.max(1, (module.order || 1) - 1)
                                )
                              }
                              disabled={module.order <= 1}
                            >
                              ↑
                            </Button>
                            <input
                              type="number"
                              min="1"
                              value={module.order || 1}
                              onChange={(e) => {
                                const newOrder = parseInt(e.target.value) || 1;
                                handleReorderModules(module._id, newOrder);
                              }}
                              className="order-input"
                            />
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() =>
                                handleReorderModules(
                                  module._id,
                                  (module.order || 1) + 1
                                )
                              }
                            >
                              ↓
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="empty-state">No modules created yet</p>
              )}
            </section>
          )}

          {/* Lectures Tab */}
          {activeTab === "lectures" && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Lectures</h2>
                <Button onClick={() => openLectureModal()}>
                  <FiPlus size={18} />
                  Schedule Lecture
                </Button>
              </div>
              {lectures.length > 0 ? (
                <Table
                  columns={[
                    { key: "topic", label: "Topic" },
                    {
                      key: "module",
                      label: "Module",
                      render: (row) => row.module?.title || "—",
                    },
                    {
                      key: "teacher",
                      label: "Teacher",
                      render: (row) =>
                        row.teacher?.user
                          ? `${row.teacher.user.firstName} ${row.teacher.user.lastName}`
                          : "—",
                    },
                    {
                      key: "startTime",
                      label: "Start Time",
                      render: (row) =>
                        row.startTime
                          ? new Date(row.startTime).toLocaleString()
                          : "—",
                    },
                    {
                      key: "endTime",
                      label: "End Time",
                      render: (row) =>
                        row.endTime
                          ? new Date(row.endTime).toLocaleString()
                          : "—",
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      render: (row) => (
                        <div className="action-buttons">
                          <Button
                            size="small"
                            variant="secondary"
                            onClick={() => openLectureModal(row)}
                          >
                            <FiEdit size={14} />
                          </Button>
                          <Button
                            size="small"
                            variant="danger"
                            onClick={() => handleDeleteLecture(row._id)}
                          >
                            <FiTrash2 size={14} />
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                  data={lectures}
                />
              ) : (
                <p className="empty-state">No lectures scheduled yet</p>
              )}
            </section>
          )}

          {/* Schedule Tab */}
          {activeTab === "schedule" && (
            <section className="detail-card">
              <h2>Schedule Timeline</h2>
              <div className="schedule-timeline">
                {lectures.length > 0 ? (
                  lectures
                    .sort(
                      (a, b) => new Date(a.startTime) - new Date(b.startTime)
                    )
                    .map((lecture) => (
                      <div key={lecture._id} className="timeline-item">
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <strong>{lecture.topic}</strong>
                            <span className="timeline-date">
                              {new Date(lecture.startTime).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="timeline-time">
                            {new Date(lecture.startTime).toLocaleTimeString()} -{" "}
                            {new Date(lecture.endTime).toLocaleTimeString()}
                          </div>
                          {lecture.module && (
                            <div className="timeline-module">
                              Module: {lecture.module.title}
                            </div>
                          )}
                          {lecture.meetingLink && (
                            <a
                              href={lecture.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="timeline-link"
                            >
                              <FiLink size={14} />
                              Join Meeting
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                ) : (
                  <p className="empty-state">No lectures scheduled</p>
                )}
              </div>
            </section>
          )}

          {/* Progress Tab */}
          {activeTab === "progress" && (
            <section className="detail-card">
              <h2>Batch Progress</h2>
              {progress && (
                <div className="progress-grid">
                  <div className="progress-card">
                    <FiLayers size={24} />
                    <div>
                      <span className="progress-label">Modules Completed</span>
                      <strong className="progress-value">
                        {progress.completedModules} / {progress.totalModules}
                      </strong>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${progress.progressPercent}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="progress-card">
                    <FiUsers size={24} />
                    <div>
                      <span className="progress-label">Total Students</span>
                      <strong className="progress-value">
                        {progress.studentsCount}
                      </strong>
                    </div>
                  </div>
                  <div className="progress-card">
                    <FiVideo size={24} />
                    <div>
                      <span className="progress-label">Total Lectures</span>
                      <strong className="progress-value">
                        {progress.lecturesCount}
                      </strong>
                    </div>
                  </div>
                  <div className="progress-card">
                    <FiClock size={24} />
                    <div>
                      <span className="progress-label">Upcoming Lectures</span>
                      <strong className="progress-value">
                        {progress.upcomingLectures}
                      </strong>
                    </div>
                  </div>
                  <div className="progress-card">
                    <FiCheck size={24} />
                    <div>
                      <span className="progress-label">Completed Lectures</span>
                      <strong className="progress-value">
                        {progress.completedLectures}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Resources Tab */}
          {activeTab === "resources" && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Resources</h2>
                <Button onClick={() => openResourceModal()}>
                  <FiPlus size={18} />
                  Add Resource
                </Button>
              </div>
              {resources.length > 0 ? (
                <div className="resources-list">
                  {resources.map((resource) => (
                    <div key={resource._id} className="resource-item">
                      <div className="resource-icon">
                        {resource.type === "link" ? (
                          <FiLink size={20} />
                        ) : (
                          <FiFileText size={20} />
                        )}
                      </div>
                      <div className="resource-content">
                        <strong>{resource.title}</strong>
                        {resource.description && <p>{resource.description}</p>}
                        {resource.url && (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link"
                          >
                            {resource.url}
                          </a>
                        )}
                      </div>
                      <div className="resource-actions">
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => openResourceModal(resource)}
                        >
                          <FiEdit size={14} />
                        </Button>
                        <Button
                          size="small"
                          variant="danger"
                          onClick={() => {
                            const updated = resources.filter(
                              (r) => r._id !== resource._id
                            );
                            adminApi.patch(`/batches/${batchId}`, {
                              resources: updated,
                            });
                            loadResources();
                          }}
                        >
                          <FiTrash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-state">No resources added yet</p>
              )}
            </section>
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <section className="detail-card">
              <h2>Batch Analytics</h2>
              {analytics && (
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <FiUsers size={24} />
                    <div>
                      <span className="analytics-label">Total Students</span>
                      <strong className="analytics-value">
                        {analytics.totalStudents}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiVideo size={24} />
                    <div>
                      <span className="analytics-label">Total Lectures</span>
                      <strong className="analytics-value">
                        {analytics.totalLectures}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiCheck size={24} />
                    <div>
                      <span className="analytics-label">
                        Completed Lectures
                      </span>
                      <strong className="analytics-value">
                        {analytics.completedLectures}
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiActivity size={24} />
                    <div>
                      <span className="analytics-label">Attendance Rate</span>
                      <strong className="analytics-value">
                        {analytics.attendanceRate}%
                      </strong>
                    </div>
                  </div>
                  <div className="analytics-card">
                    <FiTrendingUp size={24} />
                    <div>
                      <span className="analytics-label">Completion Rate</span>
                      <strong className="analytics-value">
                        {analytics.averageCompletion}%
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Stats</h3>
            <div className="stats-list">
              <div className="stat-item">
                <FiUsers size={20} />
                <div>
                  <span>Students</span>
                  <strong>
                    {batch?.studentCount ||
                      batch?.students?.length ||
                      (batch?.students ? batch.students.length : 0)}
                  </strong>
                </div>
              </div>
              <div className="stat-item">
                <FiUsers size={20} />
                <div>
                  <span>Teachers</span>
                  <strong>
                    {batch?.teacherIds?.length ||
                      batch?.teachers?.length ||
                      (batch?.teachers ? batch.teachers.length : 0)}
                  </strong>
                </div>
              </div>
              <div className="stat-item">
                <FiLayers size={20} />
                <div>
                  <span>Modules</span>
                  <strong>{modules.length}</strong>
                </div>
              </div>
              <div className="stat-item">
                <FiVideo size={20} />
                <div>
                  <span>Lectures</span>
                  <strong>{lectures.length}</strong>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Batch"
        size="medium"
      >
        <form onSubmit={handleUpdate}>
          <FormInput
            label="Batch Name"
            name="batchName"
            value={formData.batchName}
            onChange={(e) =>
              setFormData({ ...formData, batchName: e.target.value })
            }
            required
          />
          <FormInput
            label="Start Date"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={(e) =>
              setFormData({ ...formData, startDate: e.target.value })
            }
          />
          <FormInput
            label="End Date"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={(e) =>
              setFormData({ ...formData, endDate: e.target.value })
            }
          />
          <Select
            label="Status"
            name="status"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            options={[
              { value: "upcoming", label: "Upcoming" },
              { value: "ongoing", label: "Ongoing" },
              { value: "completed", label: "Completed" },
            ]}
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Update Batch
            </Button>
          </div>
        </form>
      </Modal>

      {/* Module Modal */}
      <Modal
        isOpen={showModuleModal}
        onClose={() => {
          setShowModuleModal(false);
          setSelectedModule(null);
        }}
        title={selectedModule ? "Edit Module" : "Create Module"}
        size="medium"
      >
        {batch && (
          <div className="module-context-info">
            <div className="context-breadcrumb">
              <span className="context-item">
                <strong>Course:</strong>{" "}
                {batch.course?.title || "Unknown Course"}
              </span>
              <span className="context-separator">→</span>
              <span className="context-item">
                <strong>Batch:</strong> {batch.batchName}
              </span>
              <span className="context-separator">→</span>
              <span className="context-item active">
                <strong>Module:</strong>{" "}
                {selectedModule ? selectedModule.title : "New Module"}
              </span>
            </div>
            <div className="context-note">
              <p>
                ✓ This module will be created for{" "}
                <strong>{batch.batchName}</strong> batch in{" "}
                <strong>{batch.course?.title || "the selected course"}</strong>
              </p>
            </div>
          </div>
        )}
        <form onSubmit={handleModuleSubmit}>
          <FormInput
            label="Module Title"
            name="title"
            value={moduleFormData.title}
            onChange={(e) =>
              setModuleFormData({ ...moduleFormData, title: e.target.value })
            }
            required
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={3}
            value={moduleFormData.description}
            onChange={(e) =>
              setModuleFormData({
                ...moduleFormData,
                description: e.target.value,
              })
            }
          />
          <FormInput
            label="Order"
            name="order"
            type="number"
            value={moduleFormData.order}
            onChange={(e) =>
              setModuleFormData({
                ...moduleFormData,
                order: parseInt(e.target.value),
              })
            }
            required
          />
          <FormInput
            label="Duration"
            name="duration"
            value={moduleFormData.duration}
            onChange={(e) =>
              setModuleFormData({
                ...moduleFormData,
                duration: e.target.value,
              })
            }
            placeholder="e.g., 2 weeks, 1 month"
          />
          <div className="form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={moduleFormData.isOpen}
                onChange={(e) =>
                  setModuleFormData({
                    ...moduleFormData,
                    isOpen: e.target.checked,
                  })
                }
              />
              <span>Module is open for students</span>
            </label>
          </div>
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowModuleModal(false);
                setSelectedModule(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={moduleLoading}>
              {selectedModule ? "Update" : "Create"} Module
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bulk Module Creation Modal */}
      <Modal
        isOpen={showBulkModuleModal}
        onClose={() => {
          setShowBulkModuleModal(false);
          setBulkModulesData([
            {
              title: "",
              description: "",
              order: 1,
              duration: "",
              isOpen: false,
            },
          ]);
        }}
        title="Bulk Create Modules"
        size="large"
      >
        <form onSubmit={handleBulkModuleSubmit}>
          <div className="bulk-modules-form">
            <div className="bulk-modules-header">
              <p>
                Add multiple modules at once. Fill in the details for each
                module.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={() => {
                  const nextOrder = bulkModulesData.length + 1;
                  setBulkModulesData([
                    ...bulkModulesData,
                    {
                      title: "",
                      description: "",
                      order: nextOrder,
                      duration: "",
                      isOpen: false,
                    },
                  ]);
                }}
              >
                <FiPlus size={16} />
                Add Another Module
              </Button>
            </div>
            <div className="bulk-modules-list">
              {bulkModulesData.map((module, index) => (
                <div key={index} className="bulk-module-item">
                  <div className="bulk-module-header">
                    <h4>Module {index + 1}</h4>
                    {bulkModulesData.length > 1 && (
                      <Button
                        type="button"
                        variant="danger"
                        size="small"
                        onClick={() => {
                          const updated = bulkModulesData.filter(
                            (_, i) => i !== index
                          );
                          // Reorder remaining modules
                          updated.forEach((m, i) => {
                            m.order = i + 1;
                          });
                          setBulkModulesData(updated);
                        }}
                      >
                        <FiX size={16} />
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="bulk-module-fields">
                    <FormInput
                      label="Title"
                      name={`title-${index}`}
                      value={module.title}
                      onChange={(e) => {
                        const updated = [...bulkModulesData];
                        updated[index].title = e.target.value;
                        setBulkModulesData(updated);
                      }}
                      required
                    />
                    <FormInput
                      label="Description"
                      name={`description-${index}`}
                      type="textarea"
                      rows={2}
                      value={module.description}
                      onChange={(e) => {
                        const updated = [...bulkModulesData];
                        updated[index].description = e.target.value;
                        setBulkModulesData(updated);
                      }}
                    />
                    <div className="bulk-module-row">
                      <FormInput
                        label="Order"
                        name={`order-${index}`}
                        type="number"
                        value={module.order}
                        onChange={(e) => {
                          const updated = [...bulkModulesData];
                          updated[index].order = parseInt(e.target.value) || 1;
                          setBulkModulesData(updated);
                        }}
                        required
                      />
                      <FormInput
                        label="Duration"
                        name={`duration-${index}`}
                        value={module.duration}
                        onChange={(e) => {
                          const updated = [...bulkModulesData];
                          updated[index].duration = e.target.value;
                          setBulkModulesData(updated);
                        }}
                        placeholder="e.g., 2 weeks"
                      />
                    </div>
                    <div className="form-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={module.isOpen}
                          onChange={(e) => {
                            const updated = [...bulkModulesData];
                            updated[index].isOpen = e.target.checked;
                            setBulkModulesData(updated);
                          }}
                        />
                        <span>Module is open for students</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowBulkModuleModal(false);
                setBulkModulesData([
                  {
                    title: "",
                    description: "",
                    order: 1,
                    duration: "",
                    isOpen: false,
                  },
                ]);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={bulkModuleLoading}>
              Create {bulkModulesData.filter((m) => m.title.trim()).length}{" "}
              Module(s)
            </Button>
          </div>
        </form>
      </Modal>

      {/* Module Detail Modal */}
      <Modal
        isOpen={showModuleDetailModal}
        onClose={() => {
          setShowModuleDetailModal(false);
          setSelectedModule(null);
        }}
        title="Module Details"
        size="large"
      >
        {selectedModule && (
          <div className="module-detail-content">
            <div className="module-detail-header">
              <div>
                <h2>{selectedModule.title}</h2>
                <div className="module-detail-badges">
                  <StatusBadge
                    status={selectedModule.isOpen ? "active" : "closed"}
                  />
                  {(openModule?._id === selectedModule._id ||
                    openModule?.id === selectedModule._id) && (
                    <span className="currently-open-badge">
                      <FiActivity size={14} />
                      Currently Open
                    </span>
                  )}
                </div>
              </div>
              <div className="module-detail-actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowModuleDetailModal(false);
                    openModuleModal(selectedModule);
                  }}
                >
                  <FiEdit size={16} />
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    handleToggleModule(
                      selectedModule._id,
                      selectedModule.isOpen
                    );
                  }}
                >
                  {selectedModule.isOpen ? (
                    <>
                      <FiLock size={16} />
                      Close Module
                    </>
                  ) : (
                    <>
                      <FiUnlock size={16} />
                      Open Module
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="module-detail-info">
              <div className="info-row">
                <span className="info-label">Order:</span>
                <strong>{selectedModule.order}</strong>
              </div>
              {selectedModule.duration && (
                <div className="info-row">
                  <span className="info-label">Duration:</span>
                  <strong>{selectedModule.duration}</strong>
                </div>
              )}
              {selectedModule.description && (
                <div className="info-row">
                  <span className="info-label">Description:</span>
                  <p>{selectedModule.description}</p>
                </div>
              )}
            </div>

            {moduleStats[selectedModule._id] && (
              <div className="module-stats-section">
                <h3>Statistics</h3>
                <div className="module-stats-grid">
                  <div className="module-stat-card">
                    <FiVideo size={20} />
                    <div>
                      <span>Total Lectures</span>
                      <strong>
                        {moduleStats[selectedModule._id].totalLectures}
                      </strong>
                    </div>
                  </div>
                  <div className="module-stat-card">
                    <FiCheck size={20} />
                    <div>
                      <span>Completed Lectures</span>
                      <strong>
                        {moduleStats[selectedModule._id].completedLectures}
                      </strong>
                    </div>
                  </div>
                  <div className="module-stat-card">
                    <FiTrendingUp size={20} />
                    <div>
                      <span>Completion Rate</span>
                      <strong>
                        {moduleStats[selectedModule._id].completionRate}%
                      </strong>
                    </div>
                  </div>
                  <div className="module-stat-card">
                    <FiUsers size={20} />
                    <div>
                      <span>Total Students</span>
                      <strong>
                        {moduleStats[selectedModule._id].totalStudents}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="module-lectures-section">
              <h3>Lectures in this Module</h3>
              {lectures.filter(
                (l) =>
                  l.module?._id === selectedModule._id ||
                  l.module === selectedModule._id
              ).length > 0 ? (
                <div className="module-lectures-list">
                  {lectures
                    .filter(
                      (l) =>
                        l.module?._id === selectedModule._id ||
                        l.module === selectedModule._id
                    )
                    .map((lecture) => (
                      <div key={lecture._id} className="module-lecture-item">
                        <div>
                          <strong>{lecture.topic}</strong>
                          {lecture.startTime && (
                            <span>
                              {new Date(lecture.startTime).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {lecture.meetingLink && (
                          <a
                            href={lecture.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lecture-link"
                          >
                            <FiLink size={14} />
                            Join
                          </a>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <p className="empty-state">
                  No lectures scheduled for this module
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Lecture Modal */}
      <Modal
        isOpen={showLectureModal}
        onClose={() => {
          setShowLectureModal(false);
          setSelectedLecture(null);
        }}
        title={selectedLecture ? "Edit Lecture" : "Schedule Lecture"}
        size="large"
      >
        <form onSubmit={handleLectureSubmit}>
          <Select
            label="Module"
            name="module"
            value={lectureFormData.module}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                module: e.target.value,
              })
            }
            options={[
              { value: "", label: "Select Module (Optional)" },
              ...modules.map((m) => ({
                value: m._id,
                label: `${m.order}. ${m.title} ${
                  m.isOpen ? "✓ (Open)" : "🔒 (Closed)"
                }`,
              })),
            ]}
          />
          {lectureFormData.module &&
            (() => {
              const selectedModule = modules.find(
                (m) => m._id === lectureFormData.module
              );
              if (selectedModule && !selectedModule.isOpen) {
                return (
                  <div
                    style={{
                      padding: "var(--space-3)",
                      background: "rgba(251, 191, 36, 0.1)",
                      border: "1px solid rgba(251, 191, 36, 0.3)",
                      borderRadius: "var(--radius-md)",
                      marginTop: "var(--space-2)",
                      fontSize: "0.875rem",
                    }}
                  >
                    <strong style={{ color: "var(--warning, #f59e0b)" }}>
                      ⚠️ Module is Closed
                    </strong>
                    <p
                      style={{
                        margin: "var(--space-2) 0 0 0",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Students will NOT be able to see this lecture until the
                      module is opened. Make sure to open this module from the
                      Modules tab after creating the lecture.
                    </p>
                  </div>
                );
              }
              return null;
            })()}
          {/* Instructor Type Selection */}
          {(() => {
            const storedUser = localStorage.getItem("user");
            const currentUserData =
              currentUser ||
              (storedUser
                ? (() => {
                    try {
                      return JSON.parse(storedUser);
                    } catch {
                      return null;
                    }
                  })()
                : null);

            return (
              currentUserData &&
              ["admin", "superadmin"].includes(currentUserData.role) && (
                <div style={{ marginBottom: "var(--space-4)" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "var(--space-2)",
                      fontWeight: 500,
                    }}
                  >
                    Instructor Type{" "}
                    <span
                      style={{
                        color: "var(--text-secondary)",
                        fontWeight: "normal",
                      }}
                    >
                      (Who will take this lecture?)
                    </span>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "var(--space-2)",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      type="button"
                      variant={
                        instructorType === "self" ? "primary" : "secondary"
                      }
                      onClick={() => {
                        setInstructorType("self");
                        if (currentUserData) {
                          setLectureFormData((prev) => ({
                            ...prev,
                            teacher: currentUserData._id,
                          }));
                        }
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FiUser size={16} />
                      Self (I will take this lecture)
                    </Button>
                    <Button
                      type="button"
                      variant={
                        instructorType === "instructor"
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() => {
                        setInstructorType("instructor");
                        setLectureFormData((prev) => ({
                          ...prev,
                          teacher: "",
                        }));
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FiLayers size={16} />
                      Assign to Instructor
                    </Button>
                  </div>
                  {instructorType === "self" && currentUserData && (
                    <p
                      style={{
                        marginTop: "var(--space-2)",
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FiCheckCircle size={14} />
                      You ({currentUserData.firstName}{" "}
                      {currentUserData.lastName}) will be the instructor for
                      this lecture
                    </p>
                  )}
                </div>
              )
            );
          })()}

          {/* Teacher/Instructor Selection - Only show when instructor type is "instructor" or if current user is not admin/superadmin */}
          {(() => {
            const storedUser = localStorage.getItem("user");
            const currentUserData =
              currentUser ||
              (storedUser
                ? (() => {
                    try {
                      return JSON.parse(storedUser);
                    } catch {
                      return null;
                    }
                  })()
                : null);

            return (
              (instructorType === "instructor" ||
                !currentUserData ||
                !["admin", "superadmin"].includes(currentUserData?.role)) && (
                <Select
                  label={
                    currentUserData &&
                    ["admin", "superadmin"].includes(currentUserData.role)
                      ? "Select Instructor"
                      : "Teacher"
                  }
                  name="teacher"
                  value={lectureFormData.teacher}
                  onChange={(e) =>
                    setLectureFormData({
                      ...lectureFormData,
                      teacher: e.target.value,
                    })
                  }
                  options={[
                    {
                      value: "",
                      label:
                        currentUserData &&
                        ["admin", "superadmin"].includes(currentUserData.role)
                          ? "Select an instructor"
                          : "Select Teacher (Optional)",
                    },
                    ...availableTeachers.map((t) => {
                      // Handle both User objects and Teacher objects
                      const firstName = t.firstName || t.user?.firstName || "";
                      const lastName = t.lastName || t.user?.lastName || "";
                      const email = t.email || t.user?.email || "";
                      const id = t._id || t.user?._id || t.id;
                      return {
                        value: id,
                        label:
                          `${firstName} ${lastName}`.trim() ||
                          email ||
                          "Unknown",
                      };
                    }),
                  ]}
                />
              )
            );
          })()}
          <FormInput
            label="Topic"
            name="topic"
            value={lectureFormData.topic}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                topic: e.target.value,
              })
            }
            required
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={3}
            value={lectureFormData.description}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                description: e.target.value,
              })
            }
          />
          <FormInput
            label="Start Time"
            name="startTime"
            type="datetime-local"
            value={lectureFormData.startTime}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                startTime: e.target.value,
              })
            }
            required
          />
          <FormInput
            label="End Time"
            name="endTime"
            type="datetime-local"
            value={lectureFormData.endTime}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                endTime: e.target.value,
              })
            }
            required
          />

          {/* Google Meet Integration */}
          <div className="fermion-section">
            <div className="fermion-toggle-group">
              <label className="fermion-toggle-label">
                <input
                  type="checkbox"
                  name="createFermion"
                  checked={lectureFormData.createFermion}
                  onChange={(e) =>
                    setLectureFormData({
                      ...lectureFormData,
                      createFermion: e.target.checked,
                      meetingLink: e.target.checked
                        ? ""
                        : lectureFormData.meetingLink,
                    })
                  }
                  className="fermion-toggle"
                />
                <div className="fermion-toggle-content">
                  <div className="fermion-toggle-header">
                    <FiZap size={18} className="fermion-icon" />
                    <span className="fermion-toggle-title">
                      Auto-create Google Meet link
                    </span>
                  </div>
                  <p className="fermion-toggle-description">
                    Automatically create a Google Meet link via Google Calendar
                    API. The meeting link will be auto-generated.
                  </p>
                </div>
              </label>
              {lectureFormData.createFermion && (
                <div className="fermion-info-box">
                  <FiInfo size={16} />
                  <div>
                    <strong>Google Meet Benefits:</strong>
                    <ul>
                      <li>Familiar Google Meet interface for all participants</li>
                      <li>No additional software required</li>
                      <li>Built-in chat and screen sharing</li>
                      <li>Join link will be auto-populated after creation</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <FormInput
            label={`Meeting Link ${
              lectureFormData.createFermion
                ? "(Will be auto-generated)"
                : "(Optional)"
            }`}
            name="meetingLink"
            value={lectureFormData.meetingLink}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                meetingLink: e.target.value,
              })
            }
            placeholder={
              lectureFormData.createFermion
                ? "Google Meet link will be generated automatically"
                : "https://meet.google.com/..."
            }
            disabled={lectureFormData.createFermion}
          />
          <FormInput
            label="Recording Link"
            name="recordingLink"
            value={lectureFormData.recordingLink}
            onChange={(e) =>
              setLectureFormData({
                ...lectureFormData,
                recordingLink: e.target.value,
              })
            }
            placeholder="https://youtube.com/..."
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowLectureModal(false);
                setSelectedLecture(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" loading={lectureLoading}>
              {selectedLecture ? "Update" : "Schedule"} Lecture
            </Button>
          </div>
        </form>
      </Modal>

      {/* Resource Modal */}
      <Modal
        isOpen={showResourceModal}
        onClose={() => {
          setShowResourceModal(false);
          setSelectedResource(null);
        }}
        title={selectedResource ? "Edit Resource" : "Add Resource"}
        size="medium"
      >
        <form onSubmit={handleResourceSubmit}>
          <FormInput
            label="Title"
            name="title"
            value={resourceFormData.title}
            onChange={(e) =>
              setResourceFormData({
                ...resourceFormData,
                title: e.target.value,
              })
            }
            required
          />
          <Select
            label="Type"
            name="type"
            value={resourceFormData.type}
            onChange={(e) =>
              setResourceFormData({
                ...resourceFormData,
                type: e.target.value,
              })
            }
            options={[
              { value: "link", label: "Link" },
              { value: "file", label: "File" },
              { value: "document", label: "Document" },
            ]}
          />
          <FormInput
            label="URL"
            name="url"
            value={resourceFormData.url}
            onChange={(e) =>
              setResourceFormData({
                ...resourceFormData,
                url: e.target.value,
              })
            }
            placeholder="https://..."
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={3}
            value={resourceFormData.description}
            onChange={(e) =>
              setResourceFormData({
                ...resourceFormData,
                description: e.target.value,
              })
            }
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowResourceModal(false);
                setSelectedResource(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              {selectedResource ? "Update" : "Add"} Resource
            </Button>
          </div>
        </form>
      </Modal>

      {/* Assign Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignType(null);
          setSelectedIds([]);
        }}
        title={`Assign ${assignType === "teachers" ? "Teachers" : "Students"}`}
        size="large"
      >
        <div className="assign-content">
          <p>
            Select {assignType === "teachers" ? "teachers" : "students"} to
            assign to this batch:
          </p>
          <div className="users-select-list">
            {availableUsers.map((user) => (
              <label key={user._id} className="user-select-item">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(user._id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds([...selectedIds, user._id]);
                    } else {
                      setSelectedIds(
                        selectedIds.filter((id) => id !== user._id)
                      );
                    }
                  }}
                />
                <div>
                  <strong>
                    {user.firstName} {user.lastName}
                  </strong>
                  <span>{user.email}</span>
                </div>
              </label>
            ))}
          </div>
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                setShowAssignModal(false);
                setAssignType(null);
                setSelectedIds([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              loading={assignLoading}
              disabled={selectedIds.length === 0}
            >
              Assign {selectedIds.length}{" "}
              {assignType === "teachers" ? "Teacher(s)" : "Student(s)"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Duplicate Modal */}
      <Modal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        title="Duplicate Batch"
        size="small"
      >
        <p>Create a copy of "{batch?.batchName}"?</p>
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowDuplicateModal(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleDuplicate}>Duplicate</Button>
        </div>
      </Modal>

      {/* Template Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Save as Template"
        size="small"
      >
        <p>Save "{batch?.batchName}" as a reusable template?</p>
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveAsTemplate}>Save as Template</Button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Batch"
        size="small"
      >
        <p>Are you sure you want to delete {batch?.batchName}?</p>
        <p className="warning-text">This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BatchDetail;
